import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Helper to get a localized Dayjs object
const getLocalObj = (date: string | Dayjs) => dayjs.utc(date).local();

export default function transformDate(
  date: string,
  opt?: { fullMonth: boolean }
) {
  const dateObj = getLocalObj(date);
  if (!dateObj.isValid()) return '--';

  return dateObj.format(opt?.fullMonth ? 'DD MMMM, YYYY' : 'DD MMM, YYYY');
}

export const transformDataAndTime = (
  date: string | Dayjs, // Allow Dayjs object to avoid re-parsing
  opt?: { fullMonth: boolean }
) => {
  const dateObj = dayjs.isDayjs(date) ? date : getLocalObj(date);
  if (!dateObj.isValid()) return '--';

  const formatStr = opt?.fullMonth
    ? 'DD MMMM, YYYY, hh:mm A'
    : 'DD MMM, YYYY, hh:mm A';
  return dateObj.format(formatStr);
};

export const transformTime = (date: string) => {
  const dateObj = getLocalObj(date);
  return dateObj.isValid() ? dateObj.format('hh:mm A') : '--';
};

export function transformToAge(
  dateString: string,
  opt?: { fullMonth?: boolean; maxDayOld?: number }
): string {
  const now = dayjs().local(); // Current time is already local
  const date = getLocalObj(dateString);

  if (!date.isValid()) return '--';

  const diffSeconds = now.diff(date, 'second');
  if (diffSeconds < 60) return `Just now`;

  const diffMinutes = now.diff(date, 'minute');
  if (diffMinutes < 60)
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;

  const diffHours = now.diff(date, 'hour');
  if (diffHours < 24)
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;

  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';

  const diffDays = now.diff(date, 'day');
  const maxDays = opt?.maxDayOld ?? 7;

  if (diffDays <= maxDays) return `${diffDays} days ago`;

  // Pass the date object directly to avoid re-parsing logic
  return transformDataAndTime(date, {
    fullMonth: opt?.fullMonth || false,
  });
}

export function transformToAgeInDays(
  dateString: string,
  opt?: { fullMonth?: boolean }
): string {
  const now = dayjs().local();
  const date = getLocalObj(dateString);

  if (!date.isValid()) return '--';

  if (now.isSame(date, 'day')) return 'Today';
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';

  return transformDataAndTime(date, {
    fullMonth: opt?.fullMonth || false,
  });
}
